import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Calendar, Mail, Phone, User, Users, MapPin, MessageSquare } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Consultation = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    preferred_dates: '',
    destination_interest: '',
    group_size: 1,
    message: '',
    subscribe_newsletter: false,
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const { subscribe_newsletter, ...consultData } = formData;
      await axios.post(`${API}/consultations`, consultData);
      if (subscribe_newsletter && consultData.email) {
        try {
          await axios.post(`${API}/newsletter/subscribe`, {
            email: consultData.email,
            name: consultData.name,
            source: 'consultation',
          });
        } catch { /* silent */ }
      }
      toast.success('Consultation request submitted! We\'ll contact you soon.');
      setFormData({
        name: '',
        email: '',
        phone: '',
        preferred_dates: '',
        destination_interest: '',
        group_size: 1,
        message: '',
        subscribe_newsletter: false,
      });
    } catch (error) {
      toast.error('Failed to submit consultation request. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="py-16" data-testid="consultation-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h1 className="text-5xl sm:text-6xl font-heading font-bold text-maroon mb-6">
            Book Your Free Consultation
          </h1>
          <p className="text-xl text-maroon/70">
            Let's create a personalized travel experience just for you. Share your dreams, and we'll 
            make them reality.
          </p>
        </div>

        <div className="card max-w-2xl mx-auto">
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="consultation-form">
            <div className="space-y-2">
              <Label htmlFor="name" className="flex items-center space-x-2">
                <User size={16} />
                <span>Full Name *</span>
              </Label>
              <Input
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
                className="input-field"
                data-testid="consultation-name-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="email" className="flex items-center space-x-2">
                  <Mail size={16} />
                  <span>Email *</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="input-field"
                  data-testid="consultation-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="flex items-center space-x-2">
                  <Phone size={16} />
                  <span>Phone *</span>
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="input-field"
                  data-testid="consultation-phone-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="preferred_dates" className="flex items-center space-x-2">
                <Calendar size={16} />
                <span>Preferred Travel Dates *</span>
              </Label>
              <Input
                id="preferred_dates"
                name="preferred_dates"
                placeholder="e.g., June 2026 or Flexible"
                value={formData.preferred_dates}
                onChange={handleChange}
                required
                className="input-field"
                data-testid="consultation-dates-input"
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label htmlFor="destination_interest" className="flex items-center space-x-2">
                  <MapPin size={16} />
                  <span>Destination Interest *</span>
                </Label>
                <Input
                  id="destination_interest"
                  name="destination_interest"
                  placeholder="e.g., Southeast Asia"
                  value={formData.destination_interest}
                  onChange={handleChange}
                  required
                  className="input-field"
                  data-testid="consultation-destination-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="group_size" className="flex items-center space-x-2">
                  <Users size={16} />
                  <span>Group Size *</span>
                </Label>
                <Input
                  id="group_size"
                  name="group_size"
                  type="number"
                  min="1"
                  value={formData.group_size}
                  onChange={handleChange}
                  required
                  className="input-field"
                  data-testid="consultation-groupsize-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="message" className="flex items-center space-x-2">
                <MessageSquare size={16} />
                <span>Tell us about your dream trip *</span>
              </Label>
              <Textarea
                id="message"
                name="message"
                value={formData.message}
                onChange={handleChange}
                required
                rows={5}
                className="bg-white border-2 border-maroon/20 focus:border-maroon focus:ring-0 rounded-sm resize-none"
                placeholder="Share your travel dreams, interests, and any special requirements..."
                data-testid="consultation-message-input"
              />
            </div>

            <label className="flex items-start gap-2 cursor-pointer text-sm text-maroon">
              <input
                type="checkbox"
                checked={formData.subscribe_newsletter}
                onChange={(e) => setFormData({ ...formData, subscribe_newsletter: e.target.checked })}
                className="mt-1"
                data-testid="consultation-subscribe-newsletter"
              />
              <span>Subscribe me to the monthly newsletter for travel inspiration & offers.</span>
            </label>

            <Button 
              type="submit" 
              className="btn-primary w-full"
              disabled={loading}
              data-testid="consultation-submit-btn"
            >
              {loading ? 'Submitting...' : 'Submit Consultation Request'}
            </Button>
          </form>
        </div>

        <div className="mt-12 text-center">
          <p className="text-maroon/70">
            Prefer to speak directly? Call us at <a href="tel:+18335688090" className="font-bold text-maroon hover:text-terracotta underline">(833) 568-8090</a> or email{' '}
            <a href="mailto:hello@chimbatravel.com" className="font-bold text-maroon hover:text-terracotta underline">hello@chimbatravel.com</a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Consultation;